import { useEffect, useRef } from 'react'
import { secureSessionStorage, secureLocalStorage } from '../../../utils/secureStorage'

interface UseSessionValidationParams {
  workspaceRouteRegex: RegExp
  pathname: string
  activeTab: boolean
  onLogout: () => void
  isBootstrapping?: boolean
}

/**
 * Custom hook to validate session storage fields continuously
 * Ensures critical data exists and logs out if any is missing
 */
export const useSessionValidation = ({
  workspaceRouteRegex,
  pathname,
  activeTab,
  onLogout,
  isBootstrapping = false
}: UseSessionValidationParams) => {
  const isLoggingOut = useRef(false)

  // Expose a function to trigger silent logout (without alert)
  const logoutSilently = () => {
    isLoggingOut.current = true
    onLogout()
  }

  useEffect(() => {
    const check = () => {
      // Skip check if logging out
      if (isLoggingOut.current) {
        return
      }

      // Skip check if bootstrapping (loading initial data)
      if (isBootstrapping) {
        return
      }

      // Skip check if not on workspace/studio route
      const currentPath = window.location.pathname
      if (!workspaceRouteRegex.test(currentPath)) {
        return
      }

      // Check if tab is active
      if (!activeTab) {
        return // Only validate if this is the active tab
      }

      // Check sessionStorage items (encrypted)
      const maintenance = secureSessionStorage.getItem('maintenance')
      const accountBlocked = secureSessionStorage.getItem('accountBlocked')
      const versionIncompatible = secureSessionStorage.getItem('versionIncompatible')

      // Check localStorage items (encrypted)
      const userData = secureLocalStorage.getItem('userData')

      // Validate userData exists and is not empty
      let isUserDataValid = false
      try {
        if (userData) {
          const parsedUserData = JSON.parse(userData)
          isUserDataValid = parsedUserData && Object.keys(parsedUserData).length > 0 && parsedUserData.email
        }
      } catch (e) {
        console.error('Failed to parse userData:', e)
      }

      // Validate sessionStorage items exist
      const isMaintenanceValid = maintenance !== null
      const isAccountBlockedValid = accountBlocked !== null
      const isVersionIncompatibleValid = versionIncompatible !== null

      // If any critical data is missing, logout
      if (!isUserDataValid || !isMaintenanceValid || !isAccountBlockedValid || !isVersionIncompatibleValid) {
        isLoggingOut.current = true
        alert('Your session has expired or is invalid. Please log in again.')
        onLogout()
      }
    }

    const id = setInterval(check, 2000)

    return () => {
      clearInterval(id)
      isLoggingOut.current = false
    }
  }, [pathname, activeTab, onLogout, isBootstrapping])

  return { logoutSilently }
}
